<template>
  <div class="view-manage-container">
    <div class="view-section">
      <div class="section-header">
        <span class="title">{{ $t('个人视图') }}</span>
        <div class="header-actions">
          <a-button
            type="link"
            size="small"
            @click="handleManageView('personal')"
          >
            <template #icon><setting-outlined /></template>
          </a-button>
          <a-button
            type="link"
            size="small"
            @click="addView('personal')"
          >
            <template #icon><plus-outlined /></template>
          </a-button>
        </div>
      </div>
      <s-table
        :selectedRowKeys="selectedRowKeys"
        :columns="viewTableColumns"
        :dataSource="personData"
        :pagination="false"
        size="small"
        :showHeader="false"
        row-key="id"
        :range-selection="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'name'">
            <div class="view-item-row">
              <span
                class="view-name"
                @click="handleApplyView(record)"
              >
                {{ record.name }}
                <a-tag
                  v-if="record.is_default"
                  color="blue"
                  size="small"
                  style="margin-left: 4px"
                >
                  {{ $t('默认') }}
                </a-tag>
                <a-tag
                  v-if="record.is_system"
                  color="orange"
                  size="small"
                  style="margin-left: 4px"
                >
                  {{ $t('系统') }}
                </a-tag>
              </span>
              <div
                class="view-actions"
                v-if="!record.is_system || is_auth"
              >
                <a-tooltip :title="$t('设为默认')">
                  <a-button
                    type="text"
                    size="small"
                    @click="handleSetDefault(record)"
                  >
                    <template #icon>
                      <star-filled
                        v-if="record.is_default"
                        style="color: #fadb14"
                      />
                      <star-outlined v-else />
                    </template>
                  </a-button>
                </a-tooltip>
                <a-button
                  type="text"
                  size="small"
                  @click="handleEditView(record)"
                >
                  <template #icon><edit-outlined /></template>
                </a-button>
                <a-button
                  type="text"
                  size="small"
                  danger
                  @click="handleDeleteView(record)"
                >
                  <template #icon><delete-outlined /></template>
                </a-button>
              </div>
              <span
                class="view-num"
                style="float: right"
                :style="
                  currentView?.id === record.id ? { color: '#1890ff', fontWeight: 'bold' } : {}
                "
                v-if="record.num"
                :title="record.num"
              >
                {{ record.num }}
              </span>
            </div>
          </template>
        </template>
      </s-table>
    </div>
    <div class="view-section">
      <div class="section-header">
        <span class="title">{{ $t('公共视图') }}</span>
        <div class="header-actions">
          <a-button
            type="link"
            size="small"
            @click="handleManageView('public')"
          >
            <template #icon><setting-outlined /></template>
          </a-button>
          <a-button
            type="link"
            size="small"
            @click="addView('public')"
          >
            <template #icon><plus-outlined /></template>
          </a-button>
        </div>
      </div>
      <s-table
        :selectedRowKeys="selectedRowKeys"
        :columns="viewTableColumns"
        :dataSource="publicData"
        :pagination="false"
        size="small"
        :showHeader="false"
        row-key="id"
        :range-selection="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'name'">
            <div class="view-item-row">
              <span
                class="view-name"
                @click="handleApplyView(record)"
              >
                {{ record.name }}
                <a-tag
                  v-if="record.is_default"
                  color="blue"
                  size="small"
                  style="margin-left: 4px"
                >
                  {{ $t('默认') }}
                </a-tag>
                <a-tag
                  v-if="record.is_system"
                  color="orange"
                  size="small"
                  style="margin-left: 4px"
                >
                  {{ $t('系统') }}
                </a-tag>
              </span>
              <div
                class="view-actions"
                v-if="!record.is_system || is_auth"
              >
                <a-tooltip :title="$t('设为默认')">
                  <a-button
                    type="text"
                    size="small"
                    @click="handleSetDefault(record)"
                  >
                    <template #icon>
                      <star-filled
                        v-if="record.is_default"
                        style="color: #fadb14"
                      />
                      <star-outlined v-else />
                    </template>
                  </a-button>
                </a-tooltip>
                <a-button
                  type="text"
                  size="small"
                  @click="handleEditView(record)"
                >
                  <template #icon><edit-outlined /></template>
                </a-button>
                <a-button
                  type="text"
                  size="small"
                  danger
                  @click="handleDeleteView(record)"
                >
                  <template #icon><delete-outlined /></template>
                </a-button>
              </div>
              <span
                class="view-num"
                style="float: right"
                :style="
                  currentView?.id === record.id ? { color: '#1890ff', fontWeight: 'bold' } : {}
                "
                v-if="record.num"
                :title="record.num"
              >
                {{ record.num }}
              </span>
            </div>
          </template>
        </template>
      </s-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, reactive, createVNode } from 'vue';
import {
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  StarOutlined,
  StarFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons-vue';
import { useI18n } from 'vue-i18n';
import { useUserStore, useEnv } from 'liyu-pc-base/init-plugin';
import {
  delete_view_settings,
  update_view_settings,
  set_view_settings_default,
  refresh_view_settings_cache,
  change_view_settings_sort,
} from 'liyu-pc-base/api/view-model';
import { message, Modal } from 'ant-design-vue';
import modalBox from 'liyu-pc-base/components/form-modal/modal-tools';
import { useTableProState } from '../utils';
import { Column } from '../typing';
import { useView } from '../hooks/view';

export default defineComponent({
  name: 'ViewsConfig',
  components: {
    SettingOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    StarOutlined,
    StarFilled,
  },
  props: {
    columns: {
      type: Array as () => Array<Column>,
      default: () => [],
    },
    view_key: {
      type: String,
      default: '',
    },
    view_flag_name: {
      type: String,
      default: '',
    },
    is_view: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const userStore = useUserStore();
    const env = useEnv();
    const current_org = userStore.currentOrg;
    const userid = userStore.userid;
    const VIEW_SYSTEM_EDIT = 'view_system_edit';
    const is_auth = ref(userStore.CHECK_AUTH(VIEW_SYSTEM_EDIT));

    const { setViewData, getViewData, viewDataList, fetchViews, currentView, isViewChange } =
      useTableProState();

    const viewTableColumns = [{ dataIndex: 'name', key: 'name' }];
    const selectedRowKeys = computed(() => {
      return [currentView.value?.id];
    });

    const personData = computed(() => {
      const list = viewDataList.value
        .filter((item: any) => !!item.user_id && item.is_system)
        .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
      const list1 = viewDataList.value
        .filter((item: any) => !!item.user_id && !item.is_system)
        .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
      return list.concat(list1);
    });

    const publicData = computed(() => {
      const list = viewDataList.value
        .filter((item: any) => !item.user_id && item.is_system)
        .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
      const list1 = viewDataList.value
        .filter((item: any) => !item.user_id && !item.is_system)
        .sort((a, b) => (a.sort == b.sort ? a.id - b.id : a.sort - b.sort));
      return list.concat(list1);
    });

    const handleApplyView = (view: any) => {
      if (currentView.value?.id === view.id) {
        currentView.value = { id: 'no-view' };
        setViewData(null);
        message.warning(t('视图已取消'));
      } else {
        currentView.value = view;
        setViewData(view.data);
        message.success(t('已应用视图：') + view.name);
      }
    };

    const handleSetDefault = async (view: any) => {
      try {
        await set_view_settings_default({
          org_id: current_org.id,
          view_settings_id: view.id,
        });
        await refresh_view_settings_cache({
          org_id: current_org.id,
          view_settings_id: view.id,
        });
        message.success(t('设置默认成功'));
        fetchViews();
      } catch (e) {
        message.error(t('设置默认失败'));
      }
    };

    const handleDeleteView = (view: any) => {
      Modal.confirm({
        title: t('确认删除视图？'),
        content: t('视图删除后不可恢复，是否确认删除？'),
        icon: createVNode(ExclamationCircleOutlined),
        onOk: async () => {
          await delete_view_settings({
            org_id: current_org.id,
            view_settings_id: view.id,
          });
          message.success(t('删除成功'));
          fetchViews();
        },
      });
    };

    const handleEditView = (record: any) => {
      const tempModal = reactive({
        open: true,
        loading: false,
        title: t('编辑视图'),
        form: {
          settings: { labelAlign: 'right', layout: 'vertical', col: [] },
          fields: [
            {
              type: 'input',
              name: 'name',
              label: t('视图名称'),
              disabled: false,
              allowClear: true,
              inputType: 'text',
              defaultValue: record.name,
              placeholder: t('请输入视图名称'),
            },
          ],
          rules: reactive({
            name: [{ required: true, message: t('请输入视图名称') }],
          }),
          model: reactive({
            name: record.name,
            is_system: [record.is_system || false],
          }),
        },
        options: {
          is_system: [{ name: t('设为系统视图'), value: true }],
        },
        ok: async (data: any) => {
          await update_view_settings({
            org_id: current_org.id,
            view_settings_id: record.id,
            name: data.name,
            is_system: data.is_system && data.is_system[0],
          });
          message.success(t('修改成功'));
          fetchViews();
        },
      });

      if (is_auth.value) {
        tempModal.form.fields.push({
          type: 'checkbox',
          name: 'is_system',
          label: '',
          disabled: false,
          allowClear: true,
          labelKey: 'name',
          valueKey: 'value',
          defaultValue: [false],
        } as any);
      }
      modalBox(tempModal);
    };
    const { addView } = useView({
      currentView,
      getViewData,
      isViewChange,
      userid,
      view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
      view_menu__flag: props.view_key,
      view_menu__name: props.view_flag_name,
      fetchViews,
      is_auth,
      t,
    });

    const handleManageView = (view_type: string) => {
      const tempModal = reactive({
        open: true,
        component: 'ViewEditModal',
        compObj: {
          title: view_type == 'personal' ? t('个人视图管理') : t('公共视图管理'),
          data: view_type == 'personal' ? personData.value : publicData.value,
        },
        ok: (ids: any) => {
          if (ids && ids.length > 0) {
            change_view_settings_sort({
              org_id: current_org.id,
              view_settings_ids: ids,
            }).then(() => {
              message.success(t('保存成功'));
              fetchViews();
            });
          } else {
            message.success(t('保存成功'));
            fetchViews();
          }
        },
        cancel: (isEdit: any) => {
          if (isEdit) {
            fetchViews();
          }
        },
      });
      modalBox(tempModal);
    };

    return {
      viewTableColumns,
      personData,
      publicData,
      handleApplyView,
      handleSetDefault,
      handleDeleteView,
      handleEditView,
      addView,
      handleManageView,
      is_auth,
      fetchViews,
      selectedRowKeys,
      currentView,
    };
  },
});
</script>

<style lang="less" scoped>
.view-manage-container {
  display: flex;
  gap: 16px;
  height: 400px;
  overflow-y: auto;

  .view-section {
    flex: 1;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 8px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #f0f0f0;

      .title {
        font-weight: bold;
      }

      .header-actions {
        display: flex;
        align-items: center;
      }
    }
  }
}

.view-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .view-name {
    cursor: pointer;
    flex: 1;
    padding: 4px 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
    &:hover {
      color: #1890ff;
    }
  }

  .view-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .view-actions {
    opacity: 1;
  }
}
.view-num {
  display: inline-block;
  white-space: nowrap;
}
</style>
